<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <div class="modal-header">
        <h2>🌡️ Müşteri Sıcaklık Detayı</h2>
        <button class="close-btn" @click="$emit('close')">✕</button>
      </div>

      <div v-if="loading" class="modal-loading">
        <div class="spinner"></div>
        <p>Veriler yükleniyor...</p>
      </div>

      <div v-else-if="customerData" class="modal-content">
        <!-- Müşteri Bilgileri -->
        <div class="customer-info-section">
          <h3>👤 Müşteri Bilgileri</h3>
          <div class="info-grid">
            <div class="info-item">
              <label>İsim:</label>
              <span>{{ customerData.name }}</span>
            </div>
            <div class="info-item">
              <label>Müşteri ID:</label>
              <span>{{ customerData.id }}</span>
            </div>
          </div>
        </div>

        <!-- Toplam Sıcaklık Skoru -->
        <div class="total-score-section">
          <div class="total-score-card">
            <div class="score-circle" :class="getScoreClass(customerData.total_health_score)">
              <div class="score-value">{{ customerData.total_health_score }}</div>
              <div class="score-label">Sıcaklık Skoru</div>
            </div>
            <div class="score-category">
              <span :class="'badge-' + getScoreClass(customerData.total_health_score)">
                {{ getCategoryText(customerData.total_health_score) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Skor Bileşenleri -->
        <div class="components-section">
          <h3>📊 Sıcaklık Bileşenleri</h3>

          <!-- Trading Activity -->
          <div class="component-card">
            <div class="component-header">
              <span class="component-icon">📈</span>
              <span class="component-title">İşlem Aktivitesi</span>
              <span class="component-score">{{ customerData.trading_activity_score }}/40</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: (customerData.trading_activity_score / 40) * 100 + '%' }"
              ></div>
            </div>
            <div class="component-details">
              <div class="detail-row">
                <span>Son Giriş:</span>
                <span class="value">{{ formatDateTime(customerData.last_login) }}</span>
              </div>
            </div>
          </div>

          <!-- Financial Health -->
          <div class="component-card">
            <div class="component-header">
              <span class="component-icon">💰</span>
              <span class="component-title">Finansal Durum</span>
              <span class="component-score">{{ customerData.financial_health_score }}/30</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: (customerData.financial_health_score / 30) * 100 + '%' }"
              ></div>
            </div>
            <div class="component-details">
              <div class="detail-row">
                <span>Toplam Yatırım:</span>
                <span class="value positive">{{ formatCurrency(customerData.total_deposits) }}</span>
              </div>
              <div class="detail-row">
                <span>Toplam Çekim:</span>
                <span class="value negative">{{ formatCurrency(customerData.total_withdrawals) }}</span>
              </div>
              <div class="detail-row">
                <span>Yatırım Sayısı:</span>
                <span class="value">{{ customerData.deposit_count || 0 }}</span>
              </div>
              <div class="detail-row">
                <span>Çekim Sayısı:</span>
                <span class="value">{{ customerData.withdrawal_count || 0 }}</span>
              </div>
              <div class="detail-row highlight">
                <span>Yatırım/Çekim Oranı:</span>
                <span class="value">{{ calculateRatio(customerData.total_deposits, customerData.total_withdrawals) }}</span>
              </div>
            </div>
          </div>

          <!-- Communication -->
          <div class="component-card">
            <div class="component-header">
              <span class="component-icon">💬</span>
              <span class="component-title">İletişim & Destek</span>
              <span class="component-score">{{ customerData.communication_score }}/20</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: (customerData.communication_score / 20) * 100 + '%' }"
              ></div>
            </div>
            <div class="component-details">
              <div class="detail-row">
                <span>Son Not Tarihi:</span>
                <span class="value">{{ formatDateTime(customerData.last_note_date) }}</span>
              </div>
            </div>
          </div>

          <!-- Customer Age -->
          <div class="component-card">
            <div class="component-header">
              <span class="component-icon">⏰</span>
              <span class="component-title">Müşteri Yaşı</span>
              <span class="component-score">{{ customerData.customer_age_score }}/10</span>
            </div>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: (customerData.customer_age_score / 10) * 100 + '%' }"
              ></div>
            </div>
            <div class="component-details">
              <div class="detail-row">
                <span>Müşteri Olma Tarihi:</span>
                <span class="value">{{ formatDateTime(customerData.customer_set_date) }}</span>
              </div>
              <div class="detail-row">
                <span>Müşteri Süresi:</span>
                <span class="value">{{ calculateCustomerAge(customerData.customer_set_date) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Recommendations -->
        <div class="recommendations-section">
          <h3>💡 Önerilen Aksiyonlar</h3>
          <div class="recommendations-list">
            <div
              v-for="(recommendation, index) in getRecommendations()"
              :key="index"
              class="recommendation-item"
              :class="recommendation.priority"
            >
              <span class="recommendation-icon">{{ recommendation.icon }}</span>
              <span class="recommendation-text">{{ recommendation.text }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-view-lead" @click="viewLeadDetail">
          👤 Müşteri Detayına Git
        </button>
        <button class="btn-close" @click="$emit('close')">Kapat</button>
      </div>
    </div>
  </div>
</template>

<script>
import http from "../services/http";

export default {
  name: "HealthScoreDetailModal",
  props: {
    customerId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      customerData: null,
      loading: false,
    };
  },
  mounted() {
    this.fetchCustomerDetail();
  },
  methods: {
    async fetchCustomerDetail() {
      this.loading = true;
      try {
        const response = await http.getCustomerHealthScore(this.customerId, {
          loginUserId: this.$root.cache.userId,
          loginUserDepartment: this.$root.cache.department,
          userGroupId: this.$root.cache.userGroupId,
        });

        if (response.data.success) {
          this.customerData = response.data.data;
        } else {
          console.error("Veri hatası:", response.data.message || "Veri yüklenemedi");
          alert(response.data.message || "Veri yüklenemedi");
          this.$emit("close");
        }
      } catch (error) {
        console.error("Müşteri sıcaklık detay hatası:", error);
        const errorMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message;
        alert("Veriler yüklenirken hata oluştu: " + errorMessage);
        this.$emit("close");
      } finally {
        this.loading = false;
      }
    },

    getScoreClass(score) {
      if (score >= 80) return "healthy";
      if (score >= 50) return "risky";
      if (score >= 30) return "critical";
      return "churn";
    },

    getCategoryText(score) {
      if (score >= 80) return "🟢 Sağlıklı";
      if (score >= 50) return "🟡 Riskli";
      if (score >= 30) return "🟠 Kritik";
      return "🔴 Kayıp Riski";
    },

    formatCurrency(amount) {
      if (!amount) return "0 TL";
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
      }).format(amount);
    },

    formatDateTime(dateStr) {
      if (!dateStr || dateStr === "0000-00-00 00:00:00") return "Kayıt yok";
      const date = new Date(dateStr);
      return new Intl.DateTimeFormat("tr-TR", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },

    calculateRatio(deposits, withdrawals) {
      if (!withdrawals || withdrawals === 0) {
        return deposits > 0 ? "∞" : "0";
      }
      return (deposits / withdrawals).toFixed(2);
    },

    calculateCustomerAge(dateStr) {
      if (!dateStr || dateStr === "0000-00-00 00:00:00") return "Bilinmiyor";
      const customerDate = new Date(dateStr);
      const now = new Date();
      const diffTime = Math.abs(now - customerDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 30) return `${diffDays} gün`;
      const months = Math.floor(diffDays / 30);
      if (months < 12) return `${months} ay`;
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return `${years} yıl ${remainingMonths} ay`;
    },

    getRecommendations() {
      if (!this.customerData) return [];

      const recommendations = [];
      const score = this.customerData.total_health_score;

      // Trading Activity Recommendations
      if (this.customerData.trading_activity_score < 20) {
        recommendations.push({
          icon: "📈",
          text: "Müşteri uzun süredir login yapmamış. Acil arama yapılmalı.",
          priority: "high",
        });
      } else if (this.customerData.trading_activity_score < 40) {
        recommendations.push({
          icon: "📊",
          text: "Trading aktivitesi düşük. Platform kullanımını hatırlatın.",
          priority: "medium",
        });
      }

      // Financial Health Recommendations
      if (this.customerData.financial_health_score < 15) {
        recommendations.push({
          icon: "💸",
          text: "Çekim/Yatırım oranı kötü. Retention stratejisi uygulayın.",
          priority: "high",
        });
      }

      // Communication Recommendations
      if (this.customerData.communication_score < 10) {
        recommendations.push({
          icon: "📞",
          text: "Uzun süredir iletişim kurulmamış. Hemen arayın.",
          priority: "high",
        });
      }

      // General Score Recommendations
      if (score < 30) {
        recommendations.push({
          icon: "🚨",
          text: "KAYIP RİSKİ! Win-back kampanyası başlatın.",
          priority: "high",
        });
      } else if (score < 50) {
        recommendations.push({
          icon: "⚠️",
          text: "Kritik seviyede. Özel ilgi gösterin.",
          priority: "high",
        });
      } else if (score >= 80) {
        recommendations.push({
          icon: "⭐",
          text: "Sağlıklı müşteri. Upsell fırsatları değerlendirin.",
          priority: "low",
        });
      }

      return recommendations;
    },

    viewLeadDetail() {
      this.$router.push(`/leadDetail/${this.customerId}`);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 24px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-loading {
  padding: 60px;
  text-align: center;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Customer Info Section */
.customer-info-section {
  margin-bottom: 24px;
}

.customer-info-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #333;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.info-item span {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* Total Score Section */
.total-score-section {
  margin-bottom: 32px;
  display: flex;
  justify-content: center;
}

.total-score-card {
  text-align: center;
}

.score-circle {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  border: 8px solid;
  background: white;
}

.score-circle.healthy {
  border-color: #4caf50;
}
.score-circle.risky {
  border-color: #ff9800;
}
.score-circle.critical {
  border-color: #ff5722;
}
.score-circle.churn {
  border-color: #f44336;
}

.score-value {
  font-size: 48px;
  font-weight: bold;
  color: #333;
}

.score-label {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
}

.score-category {
  margin-top: 8px;
}

.score-category span {
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 16px;
  color: white;
}

.badge-healthy {
  background: #4caf50;
}
.badge-risky {
  background: #ff9800;
}
.badge-critical {
  background: #ff5722;
}
.badge-churn {
  background: #f44336;
}

/* Components Section */
.components-section h3 {
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #333;
}

.component-card {
  background: #f9f9f9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.component-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.component-icon {
  font-size: 24px;
}

.component-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.component-score {
  font-size: 18px;
  font-weight: bold;
  color: #2196f3;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2196f3, #1976d2);
  transition: width 0.5s ease;
}

.component-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row.highlight {
  background: #fff3e0;
  padding: 12px;
  margin: 8px -12px 0;
  border-radius: 8px;
  border: none;
  font-weight: 600;
}

.detail-row span:first-child {
  color: #666;
}

.detail-row .value {
  font-weight: 600;
  color: #333;
}

.detail-row .value.positive {
  color: #4caf50;
}

.detail-row .value.negative {
  color: #f44336;
}

/* Recommendations Section */
.recommendations-section {
  margin-top: 24px;
}

.recommendations-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  color: #333;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.recommendation-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid;
}

.recommendation-item.high {
  background: #ffebee;
  border-color: #f44336;
}

.recommendation-item.medium {
  background: #fff3e0;
  border-color: #ff9800;
}

.recommendation-item.low {
  background: #e8f5e9;
  border-color: #4caf50;
}

.recommendation-icon {
  font-size: 24px;
}

.recommendation-text {
  flex: 1;
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-footer button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-view-lead {
  background: #2196f3;
  color: white;
}

.btn-view-lead:hover {
  background: #1976d2;
}

.btn-close {
  background: #e0e0e0;
  color: #666;
}

.btn-close:hover {
  background: #bdbdbd;
}
</style>
